// src/utils/soundEffects.js

class SoundController {
  constructor() {
    this.ctx = null;
    this.buffers = new Map();
    this.lastPlayed = new Map();
    this.cooldownMs = 50; // Prevents audio stutter if the cursor moves violently
  }

  // Initializes the AudioContext on first user gesture
  init() {
    if (!this.ctx) {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) {
        this.ctx = new AudioCtx();
      }
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
  }

  // Preloads and decodes an audio file into RAM buffer
  async loadSound(name, url) {
    try {
      this.init();
      if (!this.ctx) return;
      const res = await fetch(url);
      const arrayBuffer = await res.arrayBuffer();
      const audioBuffer = await this.ctx.decodeAudioData(arrayBuffer);
      this.buffers.set(name, audioBuffer);
    } catch (err) {
      console.warn(`Could not preload audio: ${name}`, err);
    }
  }

  // Plays the decoded buffer instantly
  play(name, volume = 0.3) {
    const now = performance.now();
    const last = this.lastPlayed.get(name) || 0;

    // Rate-limit rapid triggers to maintain 60 FPS
    if (now - last < this.cooldownMs) return;
    this.lastPlayed.set(name, now);

    if (!this.ctx || !this.buffers.has(name)) return;

    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }

    const source = this.ctx.createBufferSource();
    source.buffer = this.buffers.get(name);

    const gainNode = this.ctx.createGain();
    gainNode.gain.setValueAtTime(volume, this.ctx.currentTime);

    source.connect(gainNode);
    gainNode.connect(this.ctx.destination);

    // Garbage-collection cleanup
    source.onended = () => {
      source.disconnect();
      gainNode.disconnect();
    };

    source.start(0);
  }
}

export const sounds = new SoundController();